import React from 'react';
import { useBooking } from '../context/BookingContext';
import { calculateRoomPrice } from '../utils/pricingCalculator';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const SHOP_ID = process.env.REACT_APP_SHOP_ID;
const RateOption = ({ rate, room, currentQuantity }) => {
  const { state, dispatch, formatCurrency, calculateNights } = useBooking();

  // --- Practical & Activity Based Inclusions ---
  const getInclusions = (rateObj) => {
    // Check if rate object has inclusions
    if (rateObj && rateObj.inclusions !== undefined) {
      // If inclusions is an array with items
      if (Array.isArray(rateObj.inclusions) && rateObj.inclusions.length > 0) {
        // Extract titles from inclusion objects
        return rateObj.inclusions
          .map(inclusion => {
            if (typeof inclusion === 'string') return inclusion;
            if (typeof inclusion === 'object') return inclusion.title || inclusion.name;
            return String(inclusion);
          })
          .filter(title => title && title.trim() !== '');
      }
      // If inclusions is an empty array, return empty array (don't show any inclusions)
      if (Array.isArray(rateObj.inclusions) && rateObj.inclusions.length === 0) {
        return [];
      }
    }

    // Only show fallback if there are NO inclusions property at all or it's null/undefined
    // Don't show fallback for empty arrays
    
    const name = rateObj.name ? rateObj.name.toLowerCase() : '';
    
    if (name.includes('ap')) {
      return [
        'All Meals Included (Breakfast, Lunch, Dinner)',
        'Gala Dinner included (Worth ₹2500)',
        'Comp. Airport Transfer (Two-way)',
        'Jeep Safari (Evening Slot)'
      ];
    } else if (name.includes('map') || name.includes('mep')) {
      return [
        'Breakfast & Dinner Buffet',
        'Bonfire & Musical Evening',
        'Guided Nature Walk / Jungle Trail',
        '15% Discount on Spa Services'
      ];
    } else if (name.includes('cp')) {
      return [
        'Buffet Breakfast',
        'Welcome Drink on Arrival (Non-Alcoholic)',
        'High Speed Wi-Fi',
        '2 Bottles Mineral Water Daily'
      ];
    } else if (name.includes('ep')) {
      return [
        'Room Only',
        'Access to Swimming Pool',
        'Gym / Fitness Centre Usage',
        'Free Parking'
      ];
    }
    
    return ['Complimentary Wi-Fi', 'Access to Hotel Amenities'];
  };

  const inclusions = getInclusions(rate);

  // --- NEW: Get next available room number ---
  const getNextAvailableRoomNumber = () => {
    const usedRoomNumbers = state.cart.map(item => item.roomNumber);
    let nextRoomNumber = 1;
    
    // Find the first unused room number
    while (usedRoomNumbers.includes(nextRoomNumber)) {
      nextRoomNumber++;
    }
    
    return nextRoomNumber;
  };

  // --- NEW: Get guest configuration for a specific room number ---
  const getGuestConfigForRoom = (roomNumber) => {
    const guestConfig = state.searchState.room_guest.find(
      guest => guest.room_no === roomNumber
    );
    
    if (guestConfig) {
      return guestConfig;
    }
    
    // Fallback to first guest configuration
    return state.searchState.room_guest[0];
  };

  // --- NEW: Get cart items for this specific rate/room combo ---
  const getCartItemsForThisRate = () => {
    return state.cart.filter(item => 
      item.id === rate.id && item.roomId === room.id
    );
  };

  // --- NEW: Get cart items for this ROOM CATEGORY (all rates in this category) ---
  const getCartItemsForThisRoomCategory = () => {
    return state.cart.filter(item => 
      item.roomId === room.id  // All rooms in this category, regardless of rate
    );
  };

  // --- NEW: Get current room count for THIS RATE ---
  const getCurrentRoomCountForThisRate = () => {
    return getCartItemsForThisRate().length;
  };

  // --- NEW: Get current room count for THIS ROOM CATEGORY (all rates) ---
  const getCurrentRoomCountForThisCategory = () => {
    return getCartItemsForThisRoomCategory().length;
  };

  const currentRoomCountForThisRate = getCurrentRoomCountForThisRate();
  const currentRoomCountForThisCategory = getCurrentRoomCountForThisCategory();

  // Calculate room limits
  const totalRoomsAvailable = room.total_rooms || 0;
  const totalRoomsSelectedGlobally = state.cart.length;
  const maxRoomsAllowed = state.searchState.room_guest.length;
  
  // FIXED: Use currentRoomCountForThisCategory for category limit check
  const isCategoryLimitReached = currentRoomCountForThisCategory >= totalRoomsAvailable;
  // const isGlobalLimitReached = totalRoomsSelectedGlobally >= maxRoomsAllowed;
  const canAddMoreInThisCategory = currentRoomCountForThisCategory < totalRoomsAvailable;


  // New Logics for the rates we shown
  const allRequiredRoomsSelected = totalRoomsSelectedGlobally >= maxRoomsAllowed;
  const isGlobalLimitReached = allRequiredRoomsSelected;

  const roomsStillNeeded = Math.max(0, maxRoomsAllowed - totalRoomsSelectedGlobally);
  const canAddAnyMoreGlobally = roomsStillNeeded > 0;


const getPricingForGuests = (adults, children, childAges = []) => {
  // console.log('🔍 GET PRICING FOR GUESTS START:', { 
  //   adults, 
  //   children, 
  //   childAges, // Add child ages to log
  //   roomName: room.name, 
  //   rateName: rate.name,
  //   rateId: rate.id,
  //   hasAdultPricing: !!rate.adult_pricing,
  //   adultPricingArray: rate.adult_pricing,
  //   hasChildPricing: !!rate.child_pricing,
  //   childPricingArray: rate.child_pricing,
  //   hasExtraChildPricing: !!rate.extra_child_pricing,
  //   extraChildPricing: rate.extra_child_pricing
  // });
  
  const maxGuestsInRoom = room.maxGuests || 3;
  let baseAdults = 0;
  let extraAdults = 0;
  
  // Check if adult_pricing has entry for the exact number of adults
  const hasExactAdultPricing = rate.adult_pricing && 
    rate.adult_pricing.some(pricing => pricing.adults_count === adults);
  
  // console.log('📊 ADULT PRICING CHECK:', {
  //   maxGuestsInRoom,
  //   hasExactAdultPricing,
  //   adultPricingEntries: rate.adult_pricing?.map(p => ({ count: p.adults_count, price: p.price }))
  // });
  
  if (hasExactAdultPricing) {
    baseAdults = adults;
    extraAdults = 0;
  } else {
    // If no exact pricing, find the closest LOWER or EQUAL adult count
    const availableAdultCounts = rate.adult_pricing.map(p => p.adults_count).sort((a, b) => a - b);
    let closestAdultCount = 0;
    
    // Find the maximum adult count that is <= the actual adult count
    for (const count of availableAdultCounts) {
      if (count <= adults && count <= maxGuestsInRoom) {
        closestAdultCount = count;
      }
    }
    
    // If no lower count found, use the minimum available
    if (closestAdultCount === 0 && availableAdultCounts.length > 0) {
      closestAdultCount = Math.min(...availableAdultCounts);
    }
    
    baseAdults = Math.min(closestAdultCount, maxGuestsInRoom);
    extraAdults = Math.max(0, adults - baseAdults);
    
    // console.log('📊 NO EXACT PRICING, FINDING CLOSEST:', {
    //   availableAdultCounts,
    //   closestAdultCount,
    //   baseAdults,
    //   extraAdults,
    //   adults,
    //   maxGuestsInRoom
    // });
  }

  // Calculate chargeable children based on age
  let chargeableChildren = 0;
  const childAgeDetails = [];
  
  if (children > 0 && childAges && childAges.length > 0) {
    // Count only children above 5 years old
    for (let i = 0; i < Math.min(children, childAges.length); i++) {
      const age = childAges[i];
      if (age > 5) {
        chargeableChildren++;
        childAgeDetails.push({ age, chargeable: true });
      } else {
        childAgeDetails.push({ age, chargeable: false });
      }
    }
    
    // If we have more children than ages specified (shouldn't happen but as fallback)
    if (children > childAges.length) {
      const remainingChildren = children - childAges.length;
      // Assume remaining children are above 5 (chargeable)
      chargeableChildren += remainingChildren;
      for (let i = 0; i < remainingChildren; i++) {
        childAgeDetails.push({ age: 'unknown', chargeable: true });
      }
    }
  } else {
    // If no ages specified, assume all children are chargeable
    chargeableChildren = Math.min(children, 2);
    childAgeDetails.push(...Array(Math.min(children, 2)).fill({ age: 'unknown', chargeable: true }));
  }
  
  // Limit chargeable children to max 2 (based on your existing logic)
  chargeableChildren = Math.min(chargeableChildren, 2);
  
  // console.log('👶 CHILD AGE ANALYSIS:', {
  //   totalChildren: children,
  //   childAges,
  //   chargeableChildren,
  //   childAgeDetails,
  //   childrenUnder5Free: children - chargeableChildren
  // });
  
  // Calculate price with guest-based pricing
  let totalOriginalPrice = 0;
  let totalDiscountedPrice = 0;

  // Calculate BASE adult pricing
  if (rate.adult_pricing && Array.isArray(rate.adult_pricing)) {
    // console.log('👨‍👩‍👧‍👦 AVAILABLE ADULT PRICING:', rate.adult_pricing);
    
    // Find pricing for baseAdults count
    let adultPricing = rate.adult_pricing.find(pricing => pricing.adults_count === baseAdults);

    // If not found, try to find the closest match
    if (!adultPricing) {
      // Sort by adult count and find the closest (not necessarily highest)
      const sortedPricing = [...rate.adult_pricing].sort((a, b) => a.adults_count - b.adults_count);
      adultPricing = sortedPricing.reduce((prev, current) => {
        const prevDiff = Math.abs(prev.adults_count - baseAdults);
        const currentDiff = Math.abs(current.adults_count - baseAdults);
        return currentDiff < prevDiff ? current : prev;
      });
    }
    
    // console.log('✅ SELECTED ADULT PRICING:', adultPricing);
    
    if (adultPricing) {
      // Add the adult pricing
      totalOriginalPrice += adultPricing.original_price || adultPricing.price;
      totalDiscountedPrice += adultPricing.price;
      
      // console.log('💰 ADULT PRICING ADDED:', {
      //   original: adultPricing.original_price || adultPricing.price,
      //   discounted: adultPricing.price,
      //   totalOriginalSoFar: totalOriginalPrice,
      //   totalDiscountedSoFar: totalDiscountedPrice
      // });
    }
  }

  // Calculate EXTRA adult pricing (for adults beyond base count)
  if (extraAdults > 0) {
    // console.log('🛏️ CHECKING FOR EXTRA BEDS:', {
    //   extraAdults,
    //   hasExtraBedPricing: !!rate.extra_bed_pricing,
    //   extraBedPricing: rate.extra_bed_pricing,
    //   isArray: Array.isArray(rate.extra_bed_pricing),
    //   length: rate.extra_bed_pricing?.length || 0
    // });
    
    if (rate.extra_bed_pricing && Array.isArray(rate.extra_bed_pricing) && rate.extra_bed_pricing.length > 0) {
      const extraBedPricing = rate.extra_bed_pricing[0]; // Get first extra bed pricing
      
      // console.log('✅ FOUND EXTRA BED PRICING:', extraBedPricing);
      
      if (extraBedPricing && extraBedPricing.adult_extra_bed_price !== undefined) {
        const extraBedPrice = extraBedPricing.adult_extra_bed_price || 0;
        
        // For extra beds, both original and discounted prices are the same
        totalOriginalPrice += (extraBedPrice * extraAdults);
        totalDiscountedPrice += (extraBedPrice * extraAdults);
        
        // console.log('💰 EXTRA BED CALCULATION:', {
        //   extraBedPrice,
        //   extraAdults,
        //   totalForExtraBeds: extraBedPrice * extraAdults,
        //   totalOriginalNow: totalOriginalPrice,
        //   totalDiscountedNow: totalDiscountedPrice
        // });
      } else {
        console.log('⚠️ Extra bed pricing object found but adult_extra_bed_price is undefined');
      }
    } else {
      console.log('❌ No extra bed pricing found or not an array');
    }
  }

  // Calculate child pricing - UPDATED with age-based logic
  if (chargeableChildren > 0) {
    // console.log('👶 CHECKING CHILD PRICING (chargeable children only):', {
    //   chargeableChildren,
    //   totalChildren: children,
    //   freeChildren: children - chargeableChildren,
    //   childAges,
    //   hasChildPricing: !!rate.child_pricing,
    //   childPricingArray: rate.child_pricing,
    //   childPricingLength: rate.child_pricing?.length || 0,
    //   hasExtraChildPricing: !!rate.extra_child_pricing,
    //   extraChildPricing: rate.extra_child_pricing
    // });
    
    // Check for extra_child_pricing first (new structure)
    if (rate.extra_child_pricing && rate.extra_child_pricing.price !== undefined) {
      const childPrice = rate.extra_child_pricing.price;
      
      // console.log('✅ USING EXTRA CHILD PRICING:', {
      //   price: childPrice,
      //   chargeableChildren,
      //   totalForChildren: childPrice * chargeableChildren
      // });
      
      // For extra child pricing, both original and discounted are the same
      totalOriginalPrice += (childPrice * chargeableChildren);
      totalDiscountedPrice += (childPrice * chargeableChildren);
      
    } 
    // Check if child_pricing exists AND has entries (not empty array)
    else if (rate.child_pricing && Array.isArray(rate.child_pricing) && rate.child_pricing.length > 0) {
      // console.log('👶 AVAILABLE CHILD PRICING:', rate.child_pricing);
      
      const childPricing = rate.child_pricing.find(pricing => pricing.children_count === chargeableChildren) || 
                          rate.child_pricing[rate.child_pricing.length - 1];
      
      // console.log('✅ SELECTED CHILD PRICING:', childPricing);
      
      if (childPricing) {
        totalOriginalPrice += childPricing.original_price || childPricing.price || 0;
        totalDiscountedPrice += childPricing.price || 0;
        
        // console.log('💰 CHILD PRICING ADDED:', {
        //   original: childPricing.original_price || childPricing.price || 0,
        //   discounted: childPricing.price || 0,
        //   totalOriginalNow: totalOriginalPrice,
        //   totalDiscountedNow: totalDiscountedPrice
        // });
      }
    } else {
      console.log('⚠️ No child pricing found (empty child_pricing array and no extra_child_pricing)');
    }
  }

  // console.log('📈 FINAL PRICE TOTALS:', {
  //   totalOriginalPrice,
  //   totalDiscountedPrice,
  //   hasDiscount: totalOriginalPrice > totalDiscountedPrice,
  //   discountAmount: totalOriginalPrice - totalDiscountedPrice,
  //   childrenDetails: {
  //     total: children,
  //     chargeable: chargeableChildren,
  //     free: children - chargeableChildren,
  //     childAges
  //   }
  // });

  return totalDiscountedPrice > 0 ? totalDiscountedPrice : 
         calculateRoomPrice(rate, adults, chargeableChildren, extraAdults);
};

// Also update the calculateGuestBasedPricing function with logs:
const calculateGuestBasedPricing = () => {
  // Get pricing for the next available room
  const nextRoomNumber = getNextAvailableRoomNumber();
  const guestConfig = getGuestConfigForRoom(nextRoomNumber);


   console.log('👶 Guest Config for Pricing:', {
    roomNumber: nextRoomNumber,
    guestConfig,
    childAges: guestConfig.child_ages || [],
    childrenCount: Math.min(guestConfig.no_of_children, 2),
    adultsCount: guestConfig.no_of_adults
  });
  
  // console.log('🎯 CALCULATE GUEST BASED PRICING:', {
  //   roomNumber: nextRoomNumber,
  //   guestConfig,
  //   rateName: rate.name,
  //   roomName: room.name,
  //   adultPricing: rate.adult_pricing,
  //   hasExtraChildPricing: !!rate.extra_child_pricing,
  //   extraChildPricing: rate.extra_child_pricing
  // });
  
  const adultsCount = guestConfig.no_of_adults;
  const childrenCount = Math.min(guestConfig.no_of_children, 2);
  const childAges = guestConfig.child_ages || [];
  const maxGuestsInRoom = room.maxGuests || 3;
  
  let baseAdults = 0;
  let extraAdults = 0;
  
  // Check if adult_pricing has entry for the exact number of adults
  const hasExactAdultPricing = rate.adult_pricing && 
    rate.adult_pricing.some(pricing => pricing.adults_count === adultsCount);
  
  if (hasExactAdultPricing) {
    baseAdults = adultsCount;
    extraAdults = 0;
  } else {
    // If no exact pricing, find the closest LOWER or EQUAL adult count
    const availableAdultCounts = rate.adult_pricing.map(p => p.adults_count).sort((a, b) => a - b);
    let closestAdultCount = 0;
    
    // Find the maximum adult count that is <= the actual adult count
    for (const count of availableAdultCounts) {
      if (count <= adultsCount && count <= maxGuestsInRoom) {
        closestAdultCount = count;
      }
    }
    
    // If no lower count found, use the minimum available
    if (closestAdultCount === 0 && availableAdultCounts.length > 0) {
      closestAdultCount = Math.min(...availableAdultCounts);
    }
    
    baseAdults = Math.min(closestAdultCount, maxGuestsInRoom);
    extraAdults = Math.max(0, adultsCount - baseAdults);
    
    // console.log('📊 NO EXACT PRICING, FINDING CLOSEST:', {
    //   availableAdultCounts,
    //   closestAdultCount,
    //   baseAdults,
    //   extraAdults,
    //   adultsCount,
    //   maxGuestsInRoom
    // });
  }
  
  // console.log('📊 GUEST CONFIG CALCULATION:', {
  //   adultsCount,
  //   childrenCount,
  //   baseAdults,
  //   extraAdults,
  //   maxGuests: maxGuestsInRoom,
  //   hasExactPricing: hasExactAdultPricing
  // });
  
  let totalOriginalPrice = 0;
  let totalDiscountedPrice = 0;

  // Calculate BASE adult pricing
  if (rate.adult_pricing && Array.isArray(rate.adult_pricing)) {
    let adultPricing = rate.adult_pricing.find(pricing => pricing.adults_count === baseAdults);

    // If not found, try to find the closest match
    if (!adultPricing) {
      // Sort by adult count and find the closest (not necessarily highest)
      const sortedPricing = [...rate.adult_pricing].sort((a, b) => a.adults_count - b.adults_count);
      adultPricing = sortedPricing.reduce((prev, current) => {
        const prevDiff = Math.abs(prev.adults_count - baseAdults);
        const currentDiff = Math.abs(current.adults_count - baseAdults);
        return currentDiff < prevDiff ? current : prev;
      });
    }
        
    // console.log('👨‍👩‍👧‍👦 BASE ADULT PRICING:', adultPricing);
    
    if (adultPricing) {
      totalOriginalPrice += adultPricing.original_price || adultPricing.price;
      totalDiscountedPrice += adultPricing.price;
    }
  }

  // Calculate EXTRA adult pricing
  if (extraAdults > 0 && rate.extra_bed_pricing && Array.isArray(rate.extra_bed_pricing)) {
    const extraBedPricing = rate.extra_bed_pricing[0];
    
    // console.log('🛏️ EXTRA BED PRICING:', extraBedPricing);
    
    if (extraBedPricing) {
      const extraBedPrice = extraBedPricing.adult_extra_bed_price || 0;
      totalOriginalPrice += (extraBedPrice * extraAdults);
      totalDiscountedPrice += (extraBedPrice * extraAdults);
      
      // console.log('💰 EXTRA BED CALCULATION:', {
      //   extraBedPrice,
      //   extraAdults,
      //   totalForExtraBeds: extraBedPrice * extraAdults
      // });
    }
  }

  // Calculate child pricing - UPDATED for extra_child_pricing
let chargeableChildren = 0;
  
  if (childrenCount > 0 && childAges.length > 0) {
    // Count only children above 5 years old
    for (let i = 0; i < Math.min(childrenCount, childAges.length); i++) {
      const age = childAges[i];
      if (age > 5) {
        chargeableChildren++;
      }
    }
    
    // If we have more children than ages specified
    if (childrenCount > childAges.length) {
      const remainingChildren = childrenCount - childAges.length;
      // Assume remaining children are above 5 (chargeable)
      chargeableChildren += remainingChildren;
    }
  } else {
    // If no ages specified, assume all children are chargeable
    chargeableChildren = childrenCount;
  }
  
  // Limit chargeable children to max 2
  chargeableChildren = Math.min(chargeableChildren, 2);
  
  // Calculate child pricing with age-based logic
  if (chargeableChildren > 0) {
    // Check for extra_child_pricing first (new structure)
    if (rate.extra_child_pricing && rate.extra_child_pricing.price !== undefined) {
      const childPrice = rate.extra_child_pricing.price;
      totalOriginalPrice += (childPrice * chargeableChildren);
      totalDiscountedPrice += (childPrice * chargeableChildren);
    } 
    // Check if child_pricing exists AND has entries (not empty array)
    else if (rate.child_pricing && Array.isArray(rate.child_pricing) && rate.child_pricing.length > 0) {
      const childPricing = rate.child_pricing.find(pricing => pricing.children_count === chargeableChildren) || 
                          rate.child_pricing[rate.child_pricing.length - 1];
      
      if (childPricing) {
        totalOriginalPrice += childPricing.original_price || 0;
        totalDiscountedPrice += childPricing.price || 0;
      }
    }
  }

  const result = {
    originalPrice: totalOriginalPrice,
    discountedPrice: totalDiscountedPrice > 0 ? totalDiscountedPrice : 
                    calculateRoomPrice(rate, adultsCount, childrenCount, extraAdults),
    hasDiscount: totalOriginalPrice > totalDiscountedPrice,
    extraAdults: extraAdults,
    extraBedPrice: rate.extra_bed_pricing?.[0]?.adult_extra_bed_price || 0,
    extraChildPrice: rate.extra_child_pricing?.price || 0,
    baseAdults: baseAdults
  };
  
  console.log('🎯 FINAL GUEST PRICING RESULT:', result);
  
  return result;
};
  const guestPricing = calculateGuestBasedPricing();
  const calculatedPrice = guestPricing.discountedPrice;

  // Get discount information from offer object
  const getDiscountInfo = () => {
    if (rate.offer && rate.offer.discount_name) {
      const { discount_type, discount_value, discount_percentage, discount_amount } = rate.offer;
      
      if (discount_type === 'percentage') {
        return {
          name: rate.offer.discount_name,
          type: 'percentage',
          value: discount_percentage,
          rawValue: discount_value,
          amount: discount_amount
        };
      } else if (discount_type === 'fixed') {
        return {
          name: rate.offer.discount_name,
          type: 'fixed',
          value: formatCurrency(discount_value),
          rawValue: discount_value,
          amount: discount_amount
        };
      }
    }
    
    // Calculate discount from guest pricing
    if (guestPricing.hasDiscount && guestPricing.originalPrice > 0) {
      const discount = ((guestPricing.originalPrice - guestPricing.discountedPrice) / guestPricing.originalPrice) * 100;
      if (discount > 0) {
        return {
          name: rate.offer.discount_name,
          type: 'percentage',
          value: `${Math.round(discount)}%`,
          rawValue: Math.round(discount)
        };
      }
    }
    
    return null;
  };

  const discountInfo = getDiscountInfo();

  // Format discount display text
  const getDiscountDisplayText = () => {
    if (!discountInfo) return null;
    
    if (discountInfo.type === 'percentage') {
      return `${discountInfo.value} OFF`;
    } else if (discountInfo.type === 'fixed') {
      return `${discountInfo.value} OFF`;
    }
    return null;
  };

  // Calculate savings amount
  const calculateSavings = () => {
    if (discountInfo) {
      if (discountInfo.type === 'percentage' && guestPricing.originalPrice > 0) {
        const discountDecimal = discountInfo.rawValue / 100;
        return guestPricing.originalPrice * discountDecimal;
      } else if (discountInfo.type === 'fixed') {
        return discountInfo.rawValue;
      }
    }
    
    // Calculate from guest pricing difference
    if (guestPricing.hasDiscount && guestPricing.originalPrice > 0) {
      return guestPricing.originalPrice - guestPricing.discountedPrice;
    }
    
    return 0;
  };

  const savingsAmount = calculateSavings();

  // --- UPDATED: handleRateAction with proper category counting ---
  // const handleRateAction = async (action) => {
  //   const { checkin, checkout } = state.searchState;
  //   if (!checkin || !checkout || checkout <= checkin) {
  //     alert("Please select valid Check-in and Check-out dates first.");
  //     return;
  //   }

  //     console.log('🔄 HANDLE RATE ACTION:', { action, rateName: rate.name, roomName: room.name });


  //   const sessionData = { ...state };

  //   if (action === 'select' || action === 'plus') {
  //     // Check global room limit
  //     if (isGlobalLimitReached) {
  //       alert(`You have already selected the maximum of ${maxRoomsAllowed} room${maxRoomsAllowed > 1 ? 's' : ''} needed for your search.`);
  //       return;
  //     }

  //     // Check category availability - FIXED: use currentRoomCountForThisCategory
  //     if (isCategoryLimitReached) {
  //       alert(`Only ${totalRoomsAvailable} room${totalRoomsAvailable !== 1 ? 's' : ''} available in ${room.name}.`);
  //       return;
  //     }

  //     // For BOTH 'select' and 'plus', assign a NEW room
  //     const nextRoomNumber = getNextAvailableRoomNumber();
  //     const guestConfig = getGuestConfigForRoom(nextRoomNumber);
      
  //     if (!guestConfig) {
  //       alert(`No guest configuration found for room ${nextRoomNumber}`);
  //       return;
  //     }

  //     // Calculate price for this specific guest configuration
  //     const roomPrice = getPricingForGuests(guestConfig.no_of_adults, guestConfig.no_of_children);


  //      console.log('📦 CART ITEM CREATION:', {
  //     adults: guestConfig.no_of_adults,
  //     children: guestConfig.no_of_children,
  //     roomPrice,
  //     baseAdults: Math.min(guestConfig.no_of_adults, room.maxGuests || 3),
  //     extraAdults: Math.max(0, guestConfig.no_of_adults - Math.min(guestConfig.no_of_adults, room.maxGuests || 3))
  //   });
      
  //     // Calculate base price for savings calculation
  //     const basePrice = rate.base_price || rate.originalPrice || roomPrice;
      
  //     // Create a new cart item for the new room
  //     const newCartItem = {
  //       id: rate.id,
  //       roomId: room.id,
  //       roomNumber: nextRoomNumber,
  //       roomName: room.name,
  //       rateName: rate.name,
  //       roomPrice: roomPrice,
  //       basePrice: basePrice,
  //       adultsCount: Math.min(guestConfig.no_of_adults, room.maxGuests || 3),
  //       childrenCount: Math.min(guestConfig.no_of_children, 2),
  //       imageUrl: room.images[0],
  //       discountInfo: discountInfo ? {
  //         name: discountInfo.name,
  //         type: discountInfo.type,
  //         value: discountInfo.value,
  //         rawValue: discountInfo.rawValue
  //       } : null,
  //       offerName: discountInfo ? discountInfo.name : null,
  //       offerFreeNights: rate.offerFreeNights || 0,
  //       quantity: 1, // Each cart item represents ONE room
  //       guestInfo: {
  //         adults: guestConfig.no_of_adults,
  //         children: guestConfig.no_of_children
  //       },
  //       originalGuestConfig: {
  //         adults: guestConfig.no_of_adults,
  //         children: guestConfig.no_of_children,
  //         roomNo: nextRoomNumber
  //       },
  //       savings: savingsAmount
  //     };
      
  //     sessionData.cart.push(newCartItem);
      
  //   } else if (action === 'minus') {
  //     // Find all items for this rate/room combo
  //     const rateItems = getCartItemsForThisRate();
      
  //     if (rateItems.length > 0) {
  //       // Remove the item with the highest room number (most recent)
  //       const lastAddedItem = rateItems.reduce((prev, current) => 
  //         (prev.roomNumber > current.roomNumber) ? prev : current
  //       );
        
  //       // Remove this item from cart
  //       const indexToRemove = sessionData.cart.findIndex(item => 
  //         item.id === lastAddedItem.id && 
  //         item.roomId === lastAddedItem.roomId && 
  //         item.roomNumber === lastAddedItem.roomNumber
  //       );
        
  //       if (indexToRemove > -1) {
  //         sessionData.cart.splice(indexToRemove, 1);
  //       }
  //     }
  //   }

  //   const nights = calculateNights();
  //   dispatch({ type: 'UPDATE_CART', payload: sessionData.cart });
    
  //   // Calculate taxes dynamically after cart update
  //   const calculateTaxesForCart = async () => {
  //     try {
  //       // Format dates for API
  //       const formatDate = (date) => {
  //         if (date instanceof Date) {
  //           return date.toISOString().split('T')[0];
  //         }
  //         return new Date(date).toISOString().split('T')[0];
  //       };

  //       const nights = calculateNights();
        
  //       // Array to store all tax API calls
  //       const taxPromises = [];
        
  //       // Create API call for each room in the cart
  //       sessionData.cart.forEach((item) => {
  //         // Pass PER NIGHT price to API
  //         const perNightPrice = item.roomPrice;
          
  //         const payload = {
  //           type: "tariff",
  //           amount: perNightPrice,
  //           check_in_date: formatDate(checkin),
  //           check_out_date: formatDate(checkout),
  //           id_shop: SHOP_ID 
  //         };

  //         // Add API call promise to array
  //         taxPromises.push(
  //           fetch(`${API_BASE_URL}/fo/mstCalculation`, {
  //             method: 'POST',
  //             headers: {
  //               'Accept': 'application/json',
  //               'Content-Type': 'application/json',
  //             },
  //             body: JSON.stringify(payload)
  //           })
  //           .then(response => response.json())
  //           .then(data => {
  //             if (data.total_tax) {
  //               const totalTax = data.total_tax;
  //               return {
  //                 roomName: item.roomName,
  //                 roomPrice: perNightPrice,
  //                 perNightPrice: perNightPrice,
  //                 taxData: {
  //                   base_amount_total: totalTax.base_amount_total || 0,
  //                   total_cgst_amount_after_tax: totalTax.total_cgst_amount_after_tax || 0,
  //                   total_sgst_amount_after_tax: totalTax.total_sgst_amount_after_tax || 0,
  //                   total_cess_amount: totalTax.total_cess_amount || 0,
  //                   grand_total: totalTax.grand_total || 0
  //                 }
  //               };
  //             }
  //             return null;
  //           })
  //         );
  //       });

  //       // Wait for all API calls to complete
  //       const responses = await Promise.all(taxPromises);
        
  //       // Filter out null responses
  //       const validResponses = responses.filter(r => r !== null);
        
  //       // Calculate cumulative totals
  //       const cumulativeTotals = {
  //         base_amount_total: 0,
  //         total_cgst_amount_after_tax: 0,
  //         total_sgst_amount_after_tax: 0,
  //         total_cess_amount: 0,
  //         grand_total: 0,
  //         perNightBaseTotal: 0
  //       };
        
  //       const roomBreakdowns = [];
        
  //       validResponses.forEach((roomData, index) => {
  //         // Add to cumulative totals
  //         cumulativeTotals.base_amount_total += roomData.taxData.base_amount_total;
  //         cumulativeTotals.total_cgst_amount_after_tax += roomData.taxData.total_cgst_amount_after_tax;
  //         cumulativeTotals.total_sgst_amount_after_tax += roomData.taxData.total_sgst_amount_after_tax;
  //         cumulativeTotals.total_cess_amount += roomData.taxData.total_cess_amount;
  //         cumulativeTotals.grand_total += roomData.taxData.grand_total;
  //         cumulativeTotals.perNightBaseTotal += roomData.perNightPrice;
          
  //         // Store per-room breakdown for display
  //         roomBreakdowns.push({
  //           roomIndex: index + 1,
  //           roomName: `${roomData.roomName} ${index + 1}`,
  //           perNightPrice: roomData.perNightPrice,
  //           totalForStay: roomData.taxData.base_amount_total,
  //           taxBreakdown: {
  //             base_amount_total: roomData.taxData.base_amount_total,
  //             total_cgst_amount_after_tax: roomData.taxData.total_cgst_amount_after_tax,
  //             total_sgst_amount_after_tax: roomData.taxData.total_sgst_amount_after_tax,
  //             total_cess_amount: roomData.taxData.total_cess_amount,
  //             grand_total: roomData.taxData.grand_total
  //           }
  //         });
  //       });
        
  //       // Calculate room rate total from cart items (per night × nights)
  //       const rateTotal = sessionData.cart.reduce((sum, item) => 
  //         sum + (item.roomPrice * nights), 0
  //       );
        
  //       const totalTaxes = cumulativeTotals.total_cgst_amount_after_tax + 
  //                         cumulativeTotals.total_sgst_amount_after_tax + 
  //                         cumulativeTotals.total_cess_amount;
        
  //       // Update state with the new totals
  //       dispatch({
  //         type: 'UPDATE_TOTALS',
  //         payload: {
  //           rateTotal: rateTotal,
  //           subtotal: cumulativeTotals.base_amount_total,
  //           taxes: totalTaxes,
  //           total: cumulativeTotals.grand_total,
  //           finalTotal: cumulativeTotals.grand_total,
  //           taxBreakdown: {
  //             cgst: cumulativeTotals.total_cgst_amount_after_tax,
  //             sgst: cumulativeTotals.total_sgst_amount_after_tax,
  //             cess: cumulativeTotals.total_cess_amount,
  //             totalTaxPercentage: totalTaxes > 0 ? ((totalTaxes / cumulativeTotals.base_amount_total) * 100).toFixed(2) : '0.00',
  //             perRoomBreakdowns: roomBreakdowns,
  //             nights: nights,
  //             perNightBaseTotal: cumulativeTotals.perNightBaseTotal
  //           }
  //         }
  //       });

  //     } catch (error) {
  //       console.error('Tax calculation error:', error);
        
  //       // Fallback to static calculation if any API call fails
  //       const nights = calculateNights();
  //       const totalRoomRate = sessionData.cart.reduce((sum, item) => 
  //         sum + (item.roomPrice * nights), 0
  //       );
  //       const rateTotal = totalRoomRate;
  //       const subtotal = rateTotal - (state.offerDiscount || 0);
  //       const taxes = subtotal * 0.18;
  //       const total = subtotal + taxes;
        
  //       dispatch({
  //         type: 'UPDATE_TOTALS',
  //         payload: {
  //           rateTotal,
  //           subtotal,
  //           taxes,
  //           total,
  //           finalTotal: total,
  //           taxBreakdown: null
  //         }
  //       });
  //     }
  //   };
    
  //   // Call the tax calculation function
  //   await calculateTaxesForCart();
  // };

const handleRateAction = async (action) => {
  const { checkin, checkout } = state.searchState;
  if (!checkin || !checkout || checkout <= checkin) {
    alert("Please select valid Check-in and Check-out dates first.");
    return;
  }

  // console.log('🔄 HANDLE RATE ACTION:', { 
  //   action, 
  //   rateName: rate.name, 
  //   roomName: room.name,
  //   requiredRooms: state.searchState.room_guest.length,
  //   availableRooms: totalRoomsAvailable,
  //   currentSelected: currentRoomCountForThisRate,
  //   totalSelectedGlobally: totalRoomsSelectedGlobally,
  //   maxRoomsAllowed: maxRoomsAllowed,
  //   roomsStillNeeded: maxRoomsAllowed - totalRoomsSelectedGlobally
  // });

  const sessionData = { ...state };
  const nights = calculateNights();

  if (action === 'select') {
    // Check if ALL required rooms are already selected globally
    if (allRequiredRoomsSelected) {
      alert(`You have already selected all ${maxRoomsAllowed} required room${maxRoomsAllowed > 1 ? 's' : ''}.`);
      return;
    }

    // Calculate how many rooms we CAN add based on availability
    const roomsNeeded = state.searchState.room_guest.length;
    const roomsAlreadySelected = currentRoomCountForThisCategory;
    const roomsToAdd = Math.min(
      roomsNeeded,
      totalRoomsAvailable - roomsAlreadySelected, // Available in this category
      roomsStillNeeded // Rooms still needed globally
    );

    // console.log('📊 SELECT ACTION CALCULATION:', {
    //   roomsNeeded,
    //   roomsAlreadySelected,
    //   roomsToAdd,
    //   totalRoomsAvailable,
    //   roomsStillNeeded,
    //   totalRoomsSelectedGlobally,
    //   maxRoomsAllowed
    // });

    if (roomsToAdd <= 0) {
      if (roomsAlreadySelected >= totalRoomsAvailable) {
        alert(`Only ${totalRoomsAvailable} room${totalRoomsAvailable !== 1 ? 's' : ''} available in ${room.name}.`);
      } else if (allRequiredRoomsSelected) {
        alert(`You have already selected all ${maxRoomsAllowed} required room${maxRoomsAllowed > 1 ? 's' : ''}.`);
      }
      return;
    }

    // Add rooms up to the limit
    const roomsAdded = [];
    
    for (let i = 0; i < roomsToAdd; i++) {
      const roomIndex = roomsAlreadySelected + i;
      // Get guest configuration for this specific room
      const guestConfig = state.searchState.room_guest[roomIndex];
       // ADD CONSOLE LOG HERE:
          console.log('👶 Guest Config with Child Ages:', {
            guestConfig,
            childAges: guestConfig.child_ages || [],
            childCount: guestConfig.no_of_children,
            adults: guestConfig.no_of_adults
          });
      if (!guestConfig) {
        console.error(`No guest config found for room ${roomIndex + 1}`);
        continue;
      }

      // Calculate price for this specific guest configuration
      const roomPrice = getPricingForGuests(guestConfig.no_of_adults, guestConfig.no_of_children,guestConfig.child_ages || [] );
      const basePrice = rate.base_price || rate.originalPrice || roomPrice;
      
      // Create cart item
      const newCartItem = {
        id: rate.id,
        roomId: room.id,
        roomNumber: roomIndex + 1,
        roomName: room.name,
        rateName: rate.name,
        roomPrice: roomPrice,
        basePrice: basePrice,
        adultsCount: Math.min(guestConfig.no_of_adults, room.maxGuests || 3),
        childrenCount: Math.min(guestConfig.no_of_children, 2),
        imageUrl: room.images[0],
        discountInfo: discountInfo ? {
          name: discountInfo.name,
          type: discountInfo.type,
          value: discountInfo.value,
          rawValue: discountInfo.rawValue
        } : null,
        offerName: discountInfo ? discountInfo.name : null,
        offerFreeNights: rate.offerFreeNights || 0,
        quantity: 1,
        guestInfo: {
          adults: guestConfig.no_of_adults,
          children: guestConfig.no_of_children
        },
        originalGuestConfig: {
          adults: guestConfig.no_of_adults,
          children: guestConfig.no_of_children,
          roomNo: roomIndex + 1
        },
        savings: savingsAmount,
        guestDisplay: `${guestConfig.no_of_adults} Adult${guestConfig.no_of_adults > 1 ? 's' : ''}${guestConfig.no_of_children > 0 ? `, ${guestConfig.no_of_children} Child${guestConfig.no_of_children > 1 ? 'ren' : ''}` : ''}`
      };
      
      sessionData.cart.push(newCartItem);
      roomsAdded.push(newCartItem);
    }

    // console.log(`✅ Selected ${roomsAdded.length} room(s) for ${room.name}:`, roomsAdded.map(r => ({
    //   roomNumber: r.roomNumber,
    //   guests: r.guestDisplay,
    //   price: r.roomPrice
    // })));

  } else if (action === 'plus') {
    // Check if ALL required rooms are already selected globally
    if (allRequiredRoomsSelected) {
      alert(`You have already selected all ${maxRoomsAllowed} required room${maxRoomsAllowed > 1 ? 's' : ''}.`);
      return;
    }

    // Check category availability
    if (isCategoryLimitReached) {
      alert(`Only ${totalRoomsAvailable} room${totalRoomsAvailable !== 1 ? 's' : ''} available in ${room.name}.`);
      return;
    }

    // Determine which guest configuration to use for the next room
    const nextRoomIndex = currentRoomCountForThisRate;
    const guestConfig = state.searchState.room_guest[nextRoomIndex];
    
    if (!guestConfig) {
      alert(`No guest configuration found for room ${nextRoomIndex + 1}`);
      return;
    }

    // Calculate price for this specific guest configuration
    const roomPrice = getPricingForGuests(guestConfig.no_of_adults, guestConfig.no_of_children, guestConfig.child_ages || []);
    const basePrice = rate.base_price || rate.originalPrice || roomPrice;
    
    // Create cart item for the new room
    const newCartItem = {
      id: rate.id,
      roomId: room.id,
      roomNumber: nextRoomIndex + 1,
      roomName: room.name,
      rateName: rate.name,
      roomPrice: roomPrice,
      basePrice: basePrice,
      adultsCount: Math.min(guestConfig.no_of_adults, room.maxGuests || 3),
      childrenCount: Math.min(guestConfig.no_of_children, 2),
      imageUrl: room.images[0],
      discountInfo: discountInfo ? {
        name: discountInfo.name,
        type: discountInfo.type,
        value: discountInfo.value,
        rawValue: discountInfo.rawValue
      } : null,
      offerName: discountInfo ? discountInfo.name : null,
      offerFreeNights: rate.offerFreeNights || 0,
      quantity: 1,
      guestInfo: {
        adults: guestConfig.no_of_adults,
        children: guestConfig.no_of_children
      },
      originalGuestConfig: {
        adults: guestConfig.no_of_adults,
        children: guestConfig.no_of_children,
        roomNo: nextRoomIndex + 1
      },
      savings: savingsAmount,
      guestDisplay: `${guestConfig.no_of_adults} Adult${guestConfig.no_of_adults > 1 ? 's' : ''}${guestConfig.no_of_children > 0 ? `, ${guestConfig.no_of_children} Child${guestConfig.no_of_children > 1 ? 'ren' : ''}` : ''}`
    };
    
    sessionData.cart.push(newCartItem);
    
    // console.log(`✅ Added 1 more room to ${room.name}:`, {
    //   roomNumber: nextRoomIndex + 1,
    //   guests: newCartItem.guestDisplay,
    //   price: roomPrice
    // });

  } else if (action === 'minus') {
    // MINUS ACTION: Remove ONE room (the highest numbered room)
    const rateItems = getCartItemsForThisRate();
    
    if (rateItems.length > 0) {
      // Find the room with the highest room number (most recent/last added)
      const lastAddedItem = rateItems.reduce((prev, current) => 
        (prev.roomNumber > current.roomNumber) ? prev : current
      );
      
      // Remove this item from cart
      const indexToRemove = sessionData.cart.findIndex(item => 
        item.id === lastAddedItem.id && 
        item.roomId === lastAddedItem.roomId && 
        item.roomNumber === lastAddedItem.roomNumber
      );
      
      if (indexToRemove > -1) {
        sessionData.cart.splice(indexToRemove, 1);
        console.log(`🗑️ Removed room ${lastAddedItem.roomNumber} from ${room.name}`);
      }
    }
  }

  dispatch({ type: 'UPDATE_CART', payload: sessionData.cart });
  
// Recalculate taxes
  const calculateTaxesForCart = async () => {
    try {
      // Format dates for API
      const formatDate = (date) => {
        if (date instanceof Date) {
          return date.toISOString().split('T')[0];
        }
        return new Date(date).toISOString().split('T')[0];
      };

      const nights = calculateNights();
      
      // Array to store all tax API calls
      const taxPromises = [];
      
      // Create API call for each room in the cart
      sessionData.cart.forEach((item) => {
        // Pass PER NIGHT price to API
        const perNightPrice = item.roomPrice;
        
        const payload = {
          type: "tariff",
          amount: perNightPrice,
          check_in_date: formatDate(checkin),
          check_out_date: formatDate(checkout),
          id_shop: SHOP_ID 
        };

        // Add API call promise to array
        taxPromises.push(
          fetch(`${API_BASE_URL}/fo/mstCalculation`, {
            method: 'POST',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload)
          })
          .then(response => response.json())
          .then(data => {
            if (data.total_tax) {
              const totalTax = data.total_tax;
              return {
                roomName: item.roomName,
                roomPrice: perNightPrice,
                perNightPrice: perNightPrice,
                taxData: {
                  base_amount_total: totalTax.base_amount_total || 0,
                  total_cgst_amount_after_tax: totalTax.total_cgst_amount_after_tax || 0,
                  total_sgst_amount_after_tax: totalTax.total_sgst_amount_after_tax || 0,
                  total_cess_amount: totalTax.total_cess_amount || 0,
                  grand_total: totalTax.grand_total || 0
                }
              };
            }
            return null;
          })
        );
      });

      // Wait for all API calls to complete
      const responses = await Promise.all(taxPromises);
      
      // Filter out null responses
      const validResponses = responses.filter(r => r !== null);
      
      // Calculate cumulative totals
      const cumulativeTotals = {
        base_amount_total: 0,
        total_cgst_amount_after_tax: 0,
        total_sgst_amount_after_tax: 0,
        total_cess_amount: 0,
        grand_total: 0,
        perNightBaseTotal: 0
      };
      
      const roomBreakdowns = [];
      
      validResponses.forEach((roomData, index) => {
        // Add to cumulative totals
        cumulativeTotals.base_amount_total += roomData.taxData.base_amount_total;
        cumulativeTotals.total_cgst_amount_after_tax += roomData.taxData.total_cgst_amount_after_tax;
        cumulativeTotals.total_sgst_amount_after_tax += roomData.taxData.total_sgst_amount_after_tax;
        cumulativeTotals.total_cess_amount += roomData.taxData.total_cess_amount;
        cumulativeTotals.grand_total += roomData.taxData.grand_total;
        cumulativeTotals.perNightBaseTotal += roomData.perNightPrice;
        
        // Store per-room breakdown for display
        roomBreakdowns.push({
          roomIndex: index + 1,
          roomName: `${roomData.roomName} ${index + 1}`,
          perNightPrice: roomData.perNightPrice,
          totalForStay: roomData.taxData.base_amount_total,
          taxBreakdown: {
            base_amount_total: roomData.taxData.base_amount_total,
            total_cgst_amount_after_tax: roomData.taxData.total_cgst_amount_after_tax,
            total_sgst_amount_after_tax: roomData.taxData.total_sgst_amount_after_tax,
            total_cess_amount: roomData.taxData.total_cess_amount,
            grand_total: roomData.taxData.grand_total
          }
        });
      });
      
      // Calculate room rate total from cart items (per night × nights)
      const rateTotal = sessionData.cart.reduce((sum, item) => 
        sum + (item.roomPrice * nights), 0
      );
      
      const totalTaxes = cumulativeTotals.total_cgst_amount_after_tax + 
                        cumulativeTotals.total_sgst_amount_after_tax + 
                        cumulativeTotals.total_cess_amount;
      
      // Update state with the new totals
      dispatch({
        type: 'UPDATE_TOTALS',
        payload: {
          rateTotal: rateTotal,
          subtotal: cumulativeTotals.base_amount_total,
          taxes: totalTaxes,
          total: cumulativeTotals.grand_total,
          finalTotal: cumulativeTotals.grand_total,
          taxBreakdown: {
            cgst: cumulativeTotals.total_cgst_amount_after_tax,
            sgst: cumulativeTotals.total_sgst_amount_after_tax,
            cess: cumulativeTotals.total_cess_amount,
            totalTaxPercentage: totalTaxes > 0 ? ((totalTaxes / cumulativeTotals.base_amount_total) * 100).toFixed(2) : '0.00',
            perRoomBreakdowns: roomBreakdowns,
            nights: nights,
            perNightBaseTotal: cumulativeTotals.perNightBaseTotal
          }
        }
      });

    } catch (error) {
      console.error('Tax calculation error:', error);
      
      // Fallback to static calculation if any API call fails
      const nights = calculateNights();
      const totalRoomRate = sessionData.cart.reduce((sum, item) => 
        sum + (item.roomPrice * nights), 0
      );
      const rateTotal = totalRoomRate;
      const subtotal = rateTotal - (state.offerDiscount || 0);
      const taxes = subtotal * 0.18;
      const total = subtotal + taxes;
      
      dispatch({
        type: 'UPDATE_TOTALS',
        payload: {
          rateTotal,
          subtotal,
          taxes,
          total,
          finalTotal: total,
          taxBreakdown: null
        }
      });
    }
  };
  
  await calculateTaxesForCart();
};


  

  // --- NEW: Get display room number for selected rooms ---
  const getDisplayRoomNumber = () => {
    const rateItems = getCartItemsForThisRate();
    if (rateItems.length > 0) {
      const roomNumbers = rateItems.map(item => item.roomNumber).sort((a, b) => a - b);
      if (roomNumbers.length === 1) {
        return `Room ${roomNumbers[0]}`;
      } else {
        return `Rooms ${roomNumbers.join(', ')}`;
      }
    }
    return null;
  };

  const displayRoomNumber = getDisplayRoomNumber();

  // Show guest breakdown for the NEXT available room
  const nextRoomGuest = getGuestConfigForRoom(getNextAvailableRoomNumber());
  const showGuestBreakdown = nextRoomGuest.no_of_adults > 1 || nextRoomGuest.no_of_children > 0;



const calculateTotalPriceForSelectedRooms = () => {
  const nights = calculateNights();
  const roomsInCart = getCartItemsForThisRate();
  
  if (roomsInCart.length > 0) {
    // Calculate total from actual cart items
    const totalPrice = roomsInCart.reduce((sum, item) => {
      return sum + (item.roomPrice);
    }, 0);
    
    // Get guest details for each selected room
    const roomDetails = roomsInCart.map((item) => {
      const guestConfig = state.searchState.room_guest.find(g => g.room_no === item.roomNumber);
      const childAges = guestConfig?.child_ages || [];
      
      // Format child age display
      let childText = '';
      if (item.childrenCount > 0) {
        const freeChildren = childAges.filter(age => age <= 5).length;
        const chargeableChildren = item.childrenCount - freeChildren;
        
        if (freeChildren > 0 && chargeableChildren > 0) {
          childText = `, ${item.childrenCount} Children (${chargeableChildren} chargeable, ${freeChildren} free)`;
        } else if (chargeableChildren > 0) {
          childText = `, ${chargeableChildren} Child${chargeableChildren > 1 ? 'ren' : ''}`;
        }
      }
      
      return {
        roomNumber: item.roomNumber,
        guests: `${item.adultsCount} Adult${item.adultsCount > 1 ? 's' : ''}${childText}`,
        price: item.roomPrice,
        childAges // Include child ages for debugging
      };
    });
    
    // Sort by room number
    roomDetails.sort((a, b) => a.roomNumber - b.roomNumber);
    
    return {
      totalPrice,
      roomCount: roomsInCart.length,
      nights,
      isExact: true,
      roomDetails
    };
  } else {
    // Calculate what WOULD be the total if we select ALL required rooms
    const roomsNeeded = state.searchState.room_guest.length;
    const actualRooms = Math.min(roomsNeeded, totalRoomsAvailable);
    
    let estimatedTotal = 0;
    const roomDetails = [];
    
    for (let i = 0; i < actualRooms; i++) {
      const guestConfig = state.searchState.room_guest[i];
      const roomPrice = getPricingForGuests(
        guestConfig.no_of_adults, 
        guestConfig.no_of_children, 
        guestConfig.child_ages || []
      );
      estimatedTotal += roomPrice;
      
      // Format child age display
      const childAges = guestConfig.child_ages || [];
      let childText = '';
      if (guestConfig.no_of_children > 0) {
        const freeChildren = childAges.filter(age => age <= 5).length;
        const chargeableChildren = Math.min(guestConfig.no_of_children, 2) - freeChildren;
        
        if (freeChildren > 0 && chargeableChildren > 0) {
          childText = `, ${guestConfig.no_of_children} Children (${chargeableChildren} chargeable, ${freeChildren} free)`;
        } else if (chargeableChildren > 0) {
          childText = `, ${chargeableChildren} Child${chargeableChildren > 1 ? 'ren' : ''}`;
        }
      }
      
      roomDetails.push({
        roomNumber: i + 1,
        guests: `${guestConfig.no_of_adults} Adult${guestConfig.no_of_adults > 1 ? 's' : ''}${childText}`,
        price: roomPrice,
        childAges // Include child ages for debugging
      });
    }
    
    return {
      totalPrice: estimatedTotal,
      roomCount: actualRooms,
      nights,
      isExact: false,
      roomDetails
    };
  }
};
const totalPriceData = calculateTotalPriceForSelectedRooms();

  return (
    <div className="rate-option-row group flex flex-col sm:flex-row sm:items-center justify-start gap-6 py-1.5 transition-colors duration-150 hover:bg-white sm:hover:shadow-sm sm:hover:-mx-4 sm:hover:px-4 sm:hover:rounded-md border-t border-gray-200 first:border-t-0">
      <div className="flex-grow mb-2 sm:mb-0 sm:min-w-[150px] sm:max-w-xs">
        <p className="text-sm font-semibold text-text-secondary transition-colors duration-150">
          {rate.name}
        </p>
      
        {inclusions && inclusions.length > 0 && (
          <div className="mt-2 flex flex-col gap-1">
            {inclusions.map((inc, index) => (
              <div key={index} className="flex items-start">
                <svg 
                  className="w-3.5 h-3.5 mr-2 text-green-600 flex-shrink-0 mt-0.5" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span className="text-xs text-text-secondary leading-snug">
                  {inc}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      
     <div className="flex-shrink-0 flex items-center justify-end flex-wrap gap-x-4 gap-y-1 ml-auto">
  <div className="flex flex-col items-end">
   
    
    {/* Total Price Display */}
    <div className="flex items-center gap-2">
      {/* Show original price with strikethrough if there's a discount */}
      {((guestPricing.hasDiscount && guestPricing.originalPrice > 0) || discountInfo) && (
        <span className="text-xs sm:text-sm text-gray-500 line-through">
          {formatCurrency(guestPricing.originalPrice *  totalPriceData.roomCount)}
        </span>
      )}
      <p className="text-lg font-bold text-text-primary -mt-0.5">
        {formatCurrency(totalPriceData.totalPrice)}
      </p>
    </div>
    
    <div className="flex flex-col items-end">
      <span className="text-[10px] text-gray-500">
        {totalPriceData.isExact ? 'Estimate for' : 'Estimate for'} {totalPriceData.roomCount} room{totalPriceData.roomCount > 1 ? 's' : ''} per night
      </span>
      
    </div>
  </div>
  
      <div className="rate-counter-controls w-28 text-center">
  {currentRoomCountForThisRate === 0 ? (
    <div className="flex flex-col items-center gap-1">
      <button
        onClick={() => handleRateAction('select')}
        disabled={allRequiredRoomsSelected || isCategoryLimitReached}
        className={`w-full text-center bg-brand-accent text-text-dark-on-accent border border-brand-accent px-3 py-1.5 rounded-md font-semibold text-sm hover:bg-brand-accent-hover transition-all duration-200 shadow-sm ${
          (allRequiredRoomsSelected || isCategoryLimitReached) ? 'opacity-50 cursor-not-allowed' : ''
        }`}
      >
        Select
      </button>

    </div>
  ) : (
    <div className="rate-counter flex flex-col items-center">
      <div className="flex items-center justify-between gap-1">
        <button
          onClick={() => handleRateAction('minus')}
          disabled={currentRoomCountForThisRate === 0}
          className={`w-8 h-8 rounded-full border border-gray-300 text-text-primary text-lg font-bold flex items-center justify-center hover:bg-slate-100 ${
            currentRoomCountForThisRate === 0 ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          -
        </button>
        <span className="rate-quantity font-bold text-lg text-text-primary w-8 text-center">
          {currentRoomCountForThisRate}
        </span>
        <button
          onClick={() => handleRateAction('plus')}
          disabled={allRequiredRoomsSelected || isCategoryLimitReached || currentRoomCountForThisRate >= Math.min(state.searchState.room_guest.length, totalRoomsAvailable)}
          className={`w-8 h-8 rounded-full border border-gray-300 text-text-primary text-lg font-bold flex items-center justify-center hover:bg-slate-100 ${
            (allRequiredRoomsSelected || isCategoryLimitReached || currentRoomCountForThisRate >= Math.min(state.searchState.room_guest.length, totalRoomsAvailable)) ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          +
        </button>
      </div>
      <div className="flex flex-col items-center mt-1">
        

      </div>
    </div>
  )}
</div>
    </div>
    </div>
  );
};

export default RateOption;